import { Types } from '@/lib/CustomFields'
import { getTypes, getUniqueValues } from '@/services/custom-fields-api.service.js'

/**
 * Canonical mapping from incoming column headers to allowed-values keys.
 * Values here align with persisted keys in `meta.allowedValues`.
 *
 * @type {Readonly<Record<string, string>>}
 */
export const ALLOWED_VALUE_HEADER_TYPES = Object.freeze({
  department: 'departments',
  role: 'roles',
  location: 'locations',
  officelocation: 'locations'
})

/**
 * Custom field types excluded from allowed-values sync.
 * These types are not used as dropdown option sources in this flow.
 *
 * @type {Set<string>}
 */
const EXCLUDED_CUSTOM_FIELD_TYPES = new Set([
  Types.Succession,
  Types.Monetary,
  Types.Skill,
  Types.PositionShortlist
])

/**
 * Normalizes a value entry into a stable display key.
 * Supports both plain strings and `{ name }` objects.
 *
 * @param {unknown} value
 * @returns {string | null}
 */
export const normalizeAllowedValueName = (value) => {
  if (value === null || value === undefined) return null

  const candidate = typeof value === 'object' ? value.name : value
  if (candidate === null || candidate === undefined) return null

  const normalized = String(candidate).trim()
  return normalized || null
}

/**
 * Converts a raw allowed value entry into the normalized persisted shape.
 * - Preserves existing metadata fields when `value` is an object.
 * - Ensures `name` is normalized and `selected` has a default.
 *
 * @param {unknown} value
 * @returns {{name: string, selected: boolean} | Record<string, unknown> | null}
 */
const normalizeAllowedValueEntry = (value) => {
  const normalizedName = normalizeAllowedValueName(value)
  if (!normalizedName) return null

  if (typeof value === 'object' && value !== null) {
    return {
      ...value,
      name: normalizedName,
      selected: value.selected ?? true
    }
  }

  return {
    name: normalizedName,
    selected: true
  }
}

/**
 * Returns custom field types that are valid for allowed-values sync.
 *
 * @param {Array<{id?: string, name?: string, type?: string}>} types
 * @returns {Array<{id: string, name: string, type: string}>}
 */
export const getEligibleCustomFieldTypes = (types = []) =>
  (types || []).filter(
    (type) => type?.name && type?.type && !EXCLUDED_CUSTOM_FIELD_TYPES.has(type.type)
  )

/**
 * Builds a case-insensitive lookup: custom field display name -> custom field id.
 *
 * @param {Array<{id: string, name: string}>} types
 * @returns {Record<string, string>}
 */
export const buildCustomFieldHeaderLookup = (types = []) => {
  const lookup = {}

  for (const type of types || []) {
    const normalizedName = String(type?.name || '')
      .trim()
      .toLowerCase()
    if (!normalizedName) continue
    lookup[normalizedName] = type.id
  }

  return lookup
}

/**
 * Resolves the target allowed-values key for a given source header.
 * Priority:
 * 1) built-in keys (department/role/location)
 * 2) matching custom field name
 *
 * @param {string} header
 * @param {Record<string, string>} customFieldHeaderLookup
 * @returns {string | null}
 */
export const getAllowedValueKeyByHeader = (header, customFieldHeaderLookup = {}) => {
  const normalizedHeader = String(header || '')
    .trim()
    .toLowerCase()
  if (!normalizedHeader) return null

  return (
    ALLOWED_VALUE_HEADER_TYPES[normalizedHeader] ||
    customFieldHeaderLookup[normalizedHeader] ||
    null
  )
}

/**
 * Adds a normalized usage value into `usageByKey`.
 * Uses `Set` per key so duplicate source values are collapsed early.
 *
 * @param {Record<string, Set<string>>} usageByKey
 * @param {string | null} key
 * @param {unknown} value
 * @returns {void}
 */
const addUsageName = (usageByKey, key, value) => {
  const normalizedValue = normalizeAllowedValueName(value)
  if (!key || !normalizedValue) return

  if (!usageByKey[key]) {
    usageByKey[key] = new Set()
  }

  usageByKey[key].add(normalizedValue)
}

/**
 * Appends raw values for a single key into the usage map.
 *
 * @param {Record<string, Set<string>>} usageByKey
 * @param {string} key
 * @param {unknown[]} values
 * @returns {void}
 */
export const appendUsageValues = (usageByKey, key, values = []) => {
  for (const value of values || []) {
    addUsageName(usageByKey, key, value)
  }
}

/**
 * Appends usage values from tabular rows.
 * Each cell is mapped by its header into a target allowed-values key.
 *
 * @param {Record<string, Set<string>>} usageByKey
 * @param {Array<Record<string, unknown>>} rows
 * @param {Record<string, string>} customFieldHeaderLookup
 * @returns {void}
 */
export const appendUsageFromRows = (usageByKey, rows = [], customFieldHeaderLookup = {}) => {
  if (!Array.isArray(rows) || rows.length === 0) return

  // Cache header resolution so each distinct header is normalized/mapped once.
  const headerKeyCache = new Map()

  for (const row of rows) {
    if (!row || typeof row !== 'object') continue

    for (const [header, value] of Object.entries(row)) {
      if (!headerKeyCache.has(header)) {
        headerKeyCache.set(header, getAllowedValueKeyByHeader(header, customFieldHeaderLookup))
      }

      const key = headerKeyCache.get(header)
      if (!key) continue

      addUsageName(usageByKey, key, value)
    }
  }
}

/**
 * Merges existing allowed-values config with usage-derived values.
 *
 * Behavior:
 * - Keeps existing value metadata when possible.
 * - Forces `selected=true` for values currently observed in source data.
 * - Prunes values not present in usage.
 * - Leaves unmanaged keys untouched.
 *
 * @param {Object} params
 * @param {Record<string, any>} params.currentAllowedValues
 * @param {Record<string, Set<string>>} params.usageByKey
 * @param {string[]} params.managedKeys
 * @returns {Record<string, any>}
 */
export const mergeAllowedValuesFromUsage = ({
  currentAllowedValues = {},
  usageByKey = {},
  managedKeys = []
}) => {
  const nextAllowedValues = { ...currentAllowedValues }
  const uniqueManagedKeys = [...new Set(managedKeys || [])]

  for (const key of uniqueManagedKeys) {
    const currentConfig = currentAllowedValues[key]
    const currentValues = Array.isArray(currentConfig?.values) ? currentConfig.values : []
    const usageSet = usageByKey[key] || new Set()
    const nextValues = buildNextAllowedValues(currentValues, usageSet)

    if (!currentConfig && nextValues.length === 0 && usageSet.size === 0) {
      continue
    }

    nextAllowedValues[key] = {
      ...currentConfig,
      isEnabled: currentConfig?.isEnabled ?? true,
      values: nextValues
    }
  }

  return nextAllowedValues
}

function buildNextAllowedValues(currentValues, usageSet) {
  const nextValues = []
  const nextValueIndexByName = new Map()

  for (const currentValue of currentValues) {
    const normalizedValue = normalizeAllowedValueEntry(currentValue)
    if (!normalizedValue) continue
    if (nextValueIndexByName.has(normalizedValue.name)) continue
    if (!usageSet.has(normalizedValue.name)) continue

    nextValueIndexByName.set(normalizedValue.name, nextValues.length)
    nextValues.push(normalizedValue)
  }

  for (const usageValue of usageSet) {
    const existingIndex = nextValueIndexByName.get(usageValue)
    if (existingIndex === undefined) {
      nextValueIndexByName.set(usageValue, nextValues.length)
      nextValues.push({ name: usageValue, selected: true })
      continue
    }

    nextValues[existingIndex] = {
      ...nextValues[existingIndex],
      selected: true
    }
  }

  return nextValues
}

/**
 * High-level helper to compute the next allowed-values object from all sources.
 * This is the main facade used by store actions.
 *
 * @param {Object} params
 * @param {Record<string, any>} params.currentAllowedValues
 * @param {unknown[]} params.departments
 * @param {unknown[]} params.roles
 * @param {unknown[]} params.locations
 * @param {Array<{id: string, name: string, type: string}>} params.customFieldTypes
 * @param {Record<string, unknown[]>} params.uniqueCustomFieldValues
 * @param {Array<Array<Record<string, unknown>>>} params.sourceRows
 * @returns {Record<string, any>}
 */
export const buildAllowedValuesFromSources = ({
  currentAllowedValues = {},
  departments = [],
  roles = [],
  locations = [],
  customFieldTypes = [],
  uniqueCustomFieldValues = {},
  sourceRows = []
}) => {
  // usageByKey is the central accumulator: allowed-value-key -> Set<valueName>.
  const usageByKey = {}

  // Base metadata values should always be considered valid usage.
  appendUsageValues(usageByKey, 'departments', departments)
  appendUsageValues(usageByKey, 'roles', roles)
  appendUsageValues(usageByKey, 'locations', locations)

  const eligibleCustomFieldTypes = getEligibleCustomFieldTypes(customFieldTypes)
  const customFieldHeaderLookup = buildCustomFieldHeaderLookup(eligibleCustomFieldTypes)

  // Unique values API provides currently known values per custom field.
  for (const type of eligibleCustomFieldTypes) {
    appendUsageValues(usageByKey, type.id, uniqueCustomFieldValues?.[type.id] || [])
  }

  // Source rows come from rate card / job catalog / (later) automapping.
  for (const rows of sourceRows || []) {
    appendUsageFromRows(usageByKey, rows || [], customFieldHeaderLookup)
  }

  const managedKeys = [
    ...Object.values(ALLOWED_VALUE_HEADER_TYPES),
    ...eligibleCustomFieldTypes.map((type) => type.id)
  ]

  return mergeAllowedValuesFromUsage({
    currentAllowedValues,
    usageByKey,
    managedKeys
  })
}

/**
 * Fetches custom-field metadata required for allowed-values sync.
 * This keeps API details outside store modules.
 *
 * @param {Object} params
 * @param {string} params.boardId - Current board id for unique values.
 * @param {string} [params.typesBoardId] - Board id used for custom field types.
 * @param {boolean} [params.requireComplete=false] - Throw when either API fails or returns an invalid payload.
 * @returns {Promise<{customFieldTypes: Array, uniqueCustomFieldValues: Record<string, unknown[]>}>}
 */
export const fetchCustomFieldSyncInputs = async ({
  boardId,
  typesBoardId,
  requireComplete = false
}) => {
  const targetTypesBoardId = typesBoardId || boardId

  const [typesResult, uniqueValuesResult] = await Promise.allSettled([
    getTypes({ boardId: targetTypesBoardId }),
    getUniqueValues({ boardId })
  ])

  const isTypesPayloadValid = typesResult.status === 'fulfilled' && Array.isArray(typesResult.value)
  const isUniqueValuesPayloadValid =
    uniqueValuesResult.status === 'fulfilled' &&
    uniqueValuesResult.value &&
    typeof uniqueValuesResult.value === 'object'

  if (requireComplete && (!isTypesPayloadValid || !isUniqueValuesPayloadValid)) {
    const failedParts = []
    if (!isTypesPayloadValid) failedParts.push('custom field types')
    if (!isUniqueValuesPayloadValid) failedParts.push('custom field unique values')
    throw new Error(`Failed to fetch ${failedParts.join(' and ')}`)
  }

  const customFieldTypes =
    typesResult.status === 'fulfilled' && Array.isArray(typesResult.value) ? typesResult.value : []
  const uniqueCustomFieldValues =
    uniqueValuesResult.status === 'fulfilled' &&
    uniqueValuesResult.value &&
    typeof uniqueValuesResult.value === 'object'
      ? uniqueValuesResult.value
      : {}

  return { customFieldTypes, uniqueCustomFieldValues }
}
